import { Badge, Button, Input } from '@axioma/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Download, X } from 'lucide-react';
import { useState } from 'react';

import { api, extractErrorMessage } from '../lib/api.js';

interface PreviewProcess {
  cnjMasked: string;
  tribunal: string;
  classe: { codigo: number; nome: string };
  assuntos: ReadonlyArray<{ codigo: number; nome: string }>;
  orgaoJulgador?: { codigo: number; nome: string };
  dataAjuizamento: string;
  movimentos: ReadonlyArray<{
    codigoTpu: number;
    nomeTpu: string;
    tipoInterno: string;
    dataHora: string;
  }>;
}

export function ImportProcessModal({ onClose }: { onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [cnj, setCnj] = useState('');
  const [tenantPartyRole, setTenantPartyRole] = useState<'AUTHOR' | 'DEFENDANT' | 'THIRD'>(
    'AUTHOR',
  );
  const [fazendaPublica, setFazendaPublica] = useState(false);
  const [preview, setPreview] = useState<PreviewProcess | null>(null);

  const lookup = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{
        found: boolean;
        process: PreviewProcess;
        movementCount: number;
      }>('/datajud/lookup', { cnj: cnj.trim() });
      return data;
    },
    onSuccess: (d) => {
      if (d.found) setPreview(d.process);
    },
  });

  const importMut = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{
        imported: boolean;
        cnj: string;
        tribunal: string;
        movementsInserted: number;
        movementsTotal: number;
      }>('/datajud/import-process', {
        cnj: cnj.trim(),
        tenantPartyRole,
        fazendaPublica,
        area: 'CIVIL',
      });
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vigilia', 'processes'] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-ax-modal flex items-start justify-center bg-black/60 px-4 py-12 backdrop-blur-sm">
      <div className="shadow-ax-xl w-full max-w-2xl rounded-ax-lg border border-ax-line bg-ax-graphite">
        <div className="flex items-center justify-between border-b border-ax-line px-6 py-4">
          <div>
            <p className="ax-eyebrow text-ax-text-tertiary">Vigília · DataJud</p>
            <h2 className="font-display text-xl tracking-tight text-ax-text-primary">
              Importar processo
            </h2>
          </div>
          <button
            type="button"
            aria-label="Fechar"
            onClick={onClose}
            className="rounded-ax-sm p-1 text-ax-text-tertiary hover:bg-ax-night hover:text-ax-text-primary"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-5 px-6 py-5">
          <div>
            <Input
              label="Número CNJ"
              placeholder="0000000-00.0000.0.00.0000"
              mono
              value={cnj}
              onChange={(e) => {
                setCnj(e.target.value);
                setPreview(null);
              }}
              hint="CNJ completo (com ou sem máscara). Buscamos no DataJud nacional."
            />
          </div>

          {!preview && (
            <Button
              variant="signature"
              loading={lookup.isPending}
              disabled={cnj.length < 20}
              onClick={() => lookup.mutate()}
            >
              Buscar no DataJud
            </Button>
          )}

          {lookup.isError && (
            <p className="text-xs text-ax-critical">{extractErrorMessage(lookup.error)}</p>
          )}

          {lookup.data && !lookup.data.found && (
            <p className="rounded-ax-sm border border-ax-line bg-ax-night px-3 py-2 text-sm text-ax-text-secondary">
              Processo não encontrado no DataJud. Verifique o número.
            </p>
          )}

          {preview && (
            <div className="space-y-4 rounded-ax-md border border-[rgba(229,165,71,0.30)] bg-[rgba(229,165,71,0.05)] p-4">
              <div className="flex items-start justify-between gap-2">
                <CheckCircle2 size={18} className="mt-0.5 flex-shrink-0 text-ax-aurora" />
                <div className="flex-1 space-y-1">
                  <p className="font-display text-sm text-ax-text-primary">{preview.cnjMasked}</p>
                  <p className="ax-body-sm text-ax-text-secondary">
                    {preview.tribunal} · {preview.classe.nome}
                  </p>
                  {preview.orgaoJulgador && (
                    <p className="ax-body-sm text-ax-text-tertiary">{preview.orgaoJulgador.nome}</p>
                  )}
                </div>
                <Badge variant="aurora" uppercase>
                  {String(preview.movimentos.length)} movimentos
                </Badge>
              </div>

              {preview.assuntos.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {preview.assuntos.slice(0, 5).map((a) => (
                    <span
                      key={a.codigo}
                      className="rounded-ax-sm bg-ax-night px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-ax-text-secondary"
                    >
                      {a.nome}
                    </span>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 border-t border-ax-line pt-3">
                <div>
                  <label className="ax-eyebrow mb-1 block text-ax-text-tertiary">Papel</label>
                  <select
                    value={tenantPartyRole}
                    onChange={(e) =>
                      setTenantPartyRole(e.target.value as 'AUTHOR' | 'DEFENDANT' | 'THIRD')
                    }
                    className="w-full rounded-ax-sm border border-ax-line bg-ax-night px-2 py-1.5 font-mono text-sm text-ax-text-primary focus:outline-none focus:ring-1 focus:ring-ax-aurora"
                  >
                    <option value="AUTHOR">Autor / Postulante</option>
                    <option value="DEFENDANT">Réu / Requerido</option>
                    <option value="THIRD">Terceiro / Interveniente</option>
                  </select>
                </div>
                <label className="flex items-end gap-2">
                  <input
                    type="checkbox"
                    checked={fazendaPublica}
                    onChange={(e) => setFazendaPublica(e.target.checked)}
                    className="h-4 w-4 accent-ax-aurora"
                  />
                  <span className="ax-body-sm text-ax-text-secondary">
                    Cliente é Fazenda Pública (multiplica prazos)
                  </span>
                </label>
              </div>

              <Button
                variant="signature"
                size="lg"
                leftIcon={<Download size={14} />}
                loading={importMut.isPending}
                onClick={() => importMut.mutate()}
                className="w-full"
              >
                Importar e iniciar monitoramento
              </Button>

              {importMut.isError && (
                <p className="text-xs text-ax-critical">{extractErrorMessage(importMut.error)}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
